import { css, cx } from "@emotion/css";
import { Button, LegacyDataTable, LegacyDataTableBody, LegacyDataTableFooter, LegacyDataTableFooterColumn, LegacyDataTableHeader, LegacyDataTableHeaderColumn, LegacyDataTableRow, LegacyDataTableRowColumn, LegacyDataTableToolsRow, } from "@octopusdeploy/design-system-components";
import { fontSize, space, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { TenantResource, EnvironmentResource, RunbooksDashboardItemResource, ProjectResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { EnvironmentChip, TenantChip } from "~/components/Chips";
import InternalLink from "~/components/Navigation/InternalLink";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { GitRefChip } from "../../Releases/GitRefChip/GitRefChip";
import { RunbookRunTaskStatusDetails } from "./RunbookRunTaskStatusDetails";
interface RunbookRunListProps {
    project: ProjectResource;
    runs: RunbooksDashboardItemResource[];
    environments: EnvironmentResource[];
    tenants: TenantResource[];
    canLoadMore: boolean;
    onLoadMore: () => void;
}
export function RunbookRunList({ project, runs, environments, tenants, canLoadMore, onLoadMore }: RunbookRunListProps) {
    const hasDatabaseRunbookRuns = runs.some((run) => !run.GitReference);
    const hasGitRunbookRuns = runs.some((run) => run.GitReference);
    const columns = 4 + (hasDatabaseRunbookRuns ? 1 : 0) + (hasGitRunbookRuns ? 1 : 0);
    const styles = {
        loadMoreContainer: css({
            padding: space[16],
        }),
        loadMoreActions: css({
            display: "flex",
            justifyContent: "center",
            textAlign: "center",
            flexDirection: "column",
            alignItems: "center",
        }),
        loadMoreSubtitle: css({
            color: themeTokens.color.text.secondary,
            fontSize: fontSize["xSmall"],
        }),
    };
    return (<div>
            <LegacyDataTable>
                <LegacyDataTableHeader>
                    <LegacyDataTableHeaderColumn>Run</LegacyDataTableHeaderColumn>
                    {hasDatabaseRunbookRuns && <LegacyDataTableHeaderColumn>Snapshot</LegacyDataTableHeaderColumn>}
                    {hasGitRunbookRuns && <LegacyDataTableHeaderColumn>Git</LegacyDataTableHeaderColumn>}
                    <LegacyDataTableHeaderColumn>Environment</LegacyDataTableHeaderColumn>
                    <LegacyDataTableHeaderColumn>Duration</LegacyDataTableHeaderColumn>
                    <LegacyDataTableHeaderColumn>Run by</LegacyDataTableHeaderColumn>
                </LegacyDataTableHeader>
                <LegacyDataTableBody>
                    {runs.map((run) => (<RunbookRun project={project} run={run} environments={environments} tenants={tenants} hasGitRunbookRuns={hasGitRunbookRuns} hasDatabaseRunbookRuns={hasDatabaseRunbookRuns}/>))}
                </LegacyDataTableBody>
                {canLoadMore ? (<LegacyDataTableFooter>
                        <LegacyDataTableToolsRow>
                            <LegacyDataTableFooterColumn colSpan={columns}>
                                <div className={styles.loadMoreContainer}>
                                    <div className={styles.loadMoreActions}>
                                        <Button importance="secondary" label="Load more" onClick={onLoadMore}/>
                                        <div className={styles.loadMoreSubtitle}>Or use filters to narrow the search results</div>
                                    </div>
                                </div>
                            </LegacyDataTableFooterColumn>
                        </LegacyDataTableToolsRow>
                    </LegacyDataTableFooter>) : null}
            </LegacyDataTable>
        </div>);
}
interface RunbookRunProps {
    project: ProjectResource;
    run: RunbooksDashboardItemResource;
    environments: EnvironmentResource[];
    tenants: TenantResource[];
    hasGitRunbookRuns: boolean;
    hasDatabaseRunbookRuns: boolean;
}
function RunbookRun({ project, run, environments, tenants, hasGitRunbookRuns: hasGitRunbooks, hasDatabaseRunbookRuns: hasDatabaseRunbooks }: RunbookRunProps) {
    const spaceAwareNavigation = useSpaceAwareNavigation();
    const environment = environments.find((x) => x.Id === run.EnvironmentId);
    const environmentChip = environment && <EnvironmentChip environmentName={environment.Name}/>;
    const tenant = tenants.find((x) => x.Id === run.TenantId);
    const tenantChip = tenant && <TenantChip tenantName={tenant.Name}/>;
    const styles = {
        middle: css({
            verticalAlign: "middle !important",
        }),
        row: css({
            "&:hover": {
                background: themeTokens.color.background.primary.hover,
                cursor: "pointer",
            },
        }),
    };
    const snapshotLink = links.runbookSnapshotRedirect.generateUrl({
        spaceId: project.SpaceId,
        runbookSnapshotId: run.RunbookSnapshotId,
    });
    const runDetailsLink = links.runbookRunPage.generateUrl({
        spaceId: project.SpaceId,
        projectSlug: project.Slug,
        runbookId: run.RunbookId,
        runbookSnapshotId: run.RunbookSnapshotId,
        runbookRunId: run.Id,
    });
    return (<LegacyDataTableRow className={cx(styles.middle, styles.row)} onClick={() => spaceAwareNavigation.navigate(runDetailsLink)}>
            <LegacyDataTableRowColumn className={styles.middle}>
                <RunbookRunTaskStatusDetails run={run} link={runDetailsLink} noPadding/>
            </LegacyDataTableRowColumn>
            {hasDatabaseRunbooks && <LegacyDataTableRowColumn className={styles.middle}>{!run.GitReference ? <InternalLink to={snapshotLink}>{run.RunbookSnapshotName}</InternalLink> : ""}</LegacyDataTableRowColumn>}
            {hasGitRunbooks && <LegacyDataTableRowColumn className={styles.middle}>{run.GitReference ? <GitRefChip vcsRef={run.GitReference} showCommit/> : ""}</LegacyDataTableRowColumn>}
            <LegacyDataTableRowColumn className={styles.middle}>
                {environmentChip} {tenantChip}
            </LegacyDataTableRowColumn>
            <LegacyDataTableRowColumn className={styles.middle}>{run.Duration}</LegacyDataTableRowColumn>
            <LegacyDataTableRowColumn className={styles.middle}>{run.RunBy}</LegacyDataTableRowColumn>
        </LegacyDataTableRow>);
}
